import { useState, useCallback } from "react";
import { message } from "antd";
import type { TableProps } from "antd/es/table";
import * as api from "../api";

/**
 * @typedef {object} RecordLogsHook
 * @property {api.RecordLogItem[]} logData - 日志数据
 * @property {boolean} loadingLogs - 日志加载状态
 * @property {{ current: number; pageSize: number; total: number }} pagination - 分页状态
 * @property {string} taskIdFilter - Task ID 过滤器
 * @property {(value: string) => void} setTaskIdFilter - 设置 Task ID 过滤器
 * @property {string} requestIdFilter - Request ID 过滤器
 * @property {(value: string) => void} setRequestIdFilter - 设置 Request ID 过滤器
 * @property {string} requestHeaderFilter - Request Header 过滤器
 * @property {(value: string) => void} setRequestHeaderFilter - 设置 Request Header 过滤器
 * @property {string} statusFilter - Status 过滤器
 * @property {(value: string) => void} setStatusFilter - 设置 Status 过滤器
 * @property {(pageNum?: number, pageSize?: number, filters?: { taskId?: string; requestId?: string; requestHeader?: string; status?: string }) => Promise<void>} fetchLogs - 获取日志函数
 * @property {TableProps<api.RecordLogItem>["onChange"]} handleTableChange - 表格变化处理函数
 * @property {() => void} handleApplyFilter - 应用过滤器函数
 */

/**
 * 管理录制日志相关逻辑的 Hook
 * @param {string | undefined} selectedEnv - 当前选中的环境
 * @returns {RecordLogsHook}
 */
export const useRecordLogs = () => {
  const [logData, setLogData] = useState<api.RecordLogItem[]>([]);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [taskIdFilter, setTaskIdFilter] = useState<string>("");
  const [requestIdFilter, setRequestIdFilter] = useState<string>("");
  const [requestHeaderFilter, setRequestHeaderFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const fetchLogs = useCallback(
    async (
      pageNum = pagination.current,
      pageSize = pagination.pageSize,
      filters: {
        taskId?: string;
        requestId?: string;
        requestHeader?: string;
        status?: string;
      } = {}
    ) => {
      setLoadingLogs(true);
      try {
        const params: api.ListRecordLogParams = {
          page_num: pageNum,
          page_size: pageSize,
        };
        if (filters.taskId) {
          params.task_id = filters.taskId;
        }
        if (filters.requestId) {
          params.request_id = filters.requestId;
        }
        if (filters.requestHeader) {
          params.request_headers = filters.requestHeader;
        }
        if (filters.status) {
          params.response_status = filters.status;
        }
        const taskId = new URLSearchParams(window.location.search).get(
          "taskId"
        );
        let res = undefined;
        if (taskId) {
          res = await api.getLogList(params);
        } else {
          res = await api.listRecordLog(params);
        }
        setLogData(res.data.items || []);
        setPagination((prev) => ({
          ...prev,
          current: res.data.page_num,
          pageSize: res.data.page_size,
          total: res.data.total_count,
        }));
      } catch (error) {
        message.error("获取录制日志失败");
        console.error(error);
        setLogData([]);
        setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
      } finally {
        setLoadingLogs(false);
      }
    },
    [pagination.current, pagination.pageSize]
  );

  const handleTableChange: TableProps<api.RecordLogItem>["onChange"] = (
    newPagination
  ) => {
    fetchLogs(newPagination.current, newPagination.pageSize, {
      taskId: taskIdFilter,
      requestId: requestIdFilter,
      requestHeader: requestHeaderFilter,
      status: statusFilter,
    });
  };

  const handleApplyFilter = () => {
    fetchLogs(1, pagination.pageSize, {
      taskId: taskIdFilter,
      requestId: requestIdFilter,
      requestHeader: requestHeaderFilter,
      status: statusFilter,
    });
  };

  return {
    logData,
    loadingLogs,
    pagination,
    taskIdFilter,
    setTaskIdFilter,
    requestIdFilter,
    setRequestIdFilter,
    requestHeaderFilter,
    setRequestHeaderFilter,
    statusFilter,
    setStatusFilter,
    fetchLogs,
    handleTableChange,
    handleApplyFilter,
  };
};
